/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { UserContext } from '../Context/User.context';
import Header from '../generic/Header.generic';
import ChecklistBadge from '../generic/ChecklistBadge.generic';
import { getInstance, updateItem } from '../services/Checklist.service';
import { subscribeChecklistEvents } from '../services/Realtime.service';
import { formatDate, formatDay, showErrorToast } from '../services/Utils.service';

const STATUSES = [
  { value: null, label: 'Non renseigné', customClass: 'checklist_status_empty' },
  { value: 'valid', label: 'Valide', customClass: 'checklist_status_valid' },
  { value: 'invalid', label: 'Non valide', customClass: 'checklist_status_invalid' },
];

/**
 * Remplissage d'une checklist depuis le mobile. Chaque point est enregistré
 * immédiatement (PATCH) et l'écran se met à jour en direct quand un autre
 * utilisateur modifie la même checklist.
 */
const ChecklistDetail = () => {
  const location = useLocation();
  const { user } = useContext<any>(UserContext);
  const [instance, setInstance] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [savingItemId, setSavingItemId] = useState<number | null>(null);
  const mounted = useRef<boolean>(true);
  const instanceId = Number(new URLSearchParams(location.search).get('id'));
  const canEdit = user?.roleName !== 'PASSENGERS';

  const load = () => {
    getInstance(instanceId).then(
      (result: any) => {
        if (mounted.current) {
          setInstance(result?.data || null);
          setLoading(false);
        }
      },
      (error: any) => {
        if (mounted.current) {
          setLoading(false);
          showErrorToast(error?.response?.data?.message || 'Unable to load checklist');
        }
      },
    );
  };

  useEffect(() => {
    mounted.current = true;
    load();
    const unsubscribe = subscribeChecklistEvents((event: any) => {
      if (event.type === 'poll' || event.instanceId === Number(instanceId)) {
        load();
      }
    });
    return () => {
      mounted.current = false;
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  const changeStatus = (item: any, value: 'valid' | 'invalid' | null) => {
    if (!canEdit || savingItemId) {
      return;
    }
    // Recliquer sur l'état actif remet le point à « non renseigné ».
    const nextStatus = item.status === value ? null : value;
    setSavingItemId(item.id);
    updateItem(instanceId, item.id, nextStatus).then(
      (result: any) => {
        if (!mounted.current) {
          return;
        }
        setInstance((prev: any) => ({
          ...prev,
          state: result.data.state,
          items: prev.items.map((current: any) =>
            current.id === item.id ? { ...current, ...result.data.item } : current,
          ),
        }));
      },
      (error: any) => {
        showErrorToast(error?.response?.data?.message || 'Unable to save checklist item');
      },
    );
    setSavingItemId(null);
  };

  return (
    <div className="page">
      <Header from="checklist" backUrl="/checklist" title="Checklist"></Header>
      {loading ? (
        <div className="checklist_loading">Loading…</div>
      ) : !instance ? (
        <div className="no_data_block">
          <img src="img/details_vol.svg" alt="Icon no data" className="icon_no_data" />
          <span className="text_no_data mt-5">Checklist not found</span>
        </div>
      ) : (
        <div className="checklist_detail">
          <div className="checklist_detail_header">
            <h2>{instance.entityLabel}</h2>
            <div className="checklist_detail_meta">
              <span>{formatDay(instance.date)}</span>
              <span>{instance.templateName || '—'}</span>
              <ChecklistBadge state={instance.state}></ChecklistBadge>
            </div>
          </div>
          {instance.items.map((item: any) => (
            <div className="checklist_item" key={item.id}>
              <span className="checklist_item_label">{item.label}</span>
              <div className="checklist_item_statuses">
                {STATUSES.map((option: any) => (
                  <button
                    type="button"
                    key={String(option.value)}
                    disabled={!canEdit || savingItemId === item.id}
                    className={`checklist_status ${option.customClass}${
                      item.status === option.value ? ' checklist_status_active' : ''
                    }`}
                    onClick={() => changeStatus(item, option.value)}>
                    {option.label}
                  </button>
                ))}
              </div>
              <span className="checklist_item_modified">
                {item.lastModifiedByEmail
                  ? `${item.lastModifiedByEmail} · ${formatDate(item.lastModifiedAt)}`
                  : 'Never modified'}
              </span>
            </div>
          ))}
          {!canEdit ? <p className="checklist_readonly">Read-only</p> : <></>}
        </div>
      )}
    </div>
  );
};

export default ChecklistDetail;
